import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global, globalMenu } from "@shared/libs/Global";
import { ValueChangedEvent } from "@widgets/SnForm";
import { ColumnConfig } from "@shared/components/Grid/types";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";
import { showPopupModal } from "@widgets/CommonModal";
import SnFileHelper from "@shared/components/SnFileHelper";
import { dateToString } from "@shared/utils/DateUtils";
import layout from "./SN_MAS_BAS_REG_002.json";
import ComCreateUi from "@menu/COM/ComCreateUi";

interface Props {
  Menuprops: menuInfo;
}

// 품목등록
const SN_MAS_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    sheet1: React.createRef(),
  });
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    sheet1: [],
  });
  const [formControls, setFormControls] = useState<any>({
    freeform1: [],
  });
  const [formDatas, setFormDatas] = useState<any>({
    freeform1: null,
  });
  const [headerInfo, setHeaderInfo] = useState<any>({
    grid1: {},
    grid2: {},
  });

  const fileHelperRef = useRef<any>(null);
  const pendingAttachRef = useRef<{
    node: any;
    prevFile: string;
    customPath: string;
  } | null>(null);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        const payload = {
          id: "codel",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_clases: ["MAS061", "MAS062", "MAS063"],
          },
        };
        const data = await fnGetTableById(payload);
        const classMap: Record<string, string> = {
          cd: "MAS061",
          dc: "MAS062",
          qt: "MAS063",
        };
        const flagMapByClass: Record<string, Record<string, string>> = {
          MAS061: {},
          MAS062: {},
          MAS063: {},
        };
        data.forEach((d: any) => {
          flagMapByClass[d.cd_clas][d.cd_flag] = d.nm_flag;
        });
        const dummyColumns = rtn.form.freeform1.map((col: ColumnConfig) => {
          const prefix = col.cd_param.split("_")[0]; // cd_dummy01 → cd
          const num = col.cd_param.slice(-2); // 01
          const cd_clas = classMap[prefix];
          if (cd_clas && flagMapByClass[cd_clas][num]) {
            return {
              ...col,
              nm_label: flagMapByClass[cd_clas][num],
              fg_visible: "Y",
            };
          }

          return col;
        });
        setFormControls({
          freeform1: dummyColumns,
        });
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          sheet1: rtn.grid.sheet1,
        });

        setHeaderInfo({
          grid1: rtn.header.grid1,
          grid2: rtn.header.grid2,
          freeform1: rtn.header.freeform1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
    onPrint,
  }));

  const onPrint = async () => {
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    if (!focusedRowNodeH) return;
    return {
      cd_item: focusedRowNodeH.data?.cd_item,
      qt_ct: focusedRowNodeH.data?.qt_ct,
      dt_today: dateToString(new Date()),
    };
  };

  const onSearch = async () => {
    const modifiedRows = gridRefs.current["grid1"].current?.getChange();
    const modifiedRows2 = gridRefs.current["grid2"].current?.getChange();

    if (
      modifiedRows?.modifiedRowcount > 0 ||
      modifiedRows2?.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );

      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const params = {
        id: "sn_mas_bas_reg_002_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(params);
      setFormDatas({
        freeform1: null,
      });
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        sheet1: data[2] || [],
      });
      setTimeout(() => {
        gridRefs.current["grid1"].current?.resetUpdate();
        gridRefs.current["grid2"].current?.resetUpdate();
      }, 0);
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = () => {
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const newRow = {
      cd_firm: userInfo.cd_firm,
      // cd_user_reg: userInfo.cd_user,
      nm_uer_reg: userInfo.nm_user,
      fg_vat: "11",
      yn_bf: "Y",
      yn_phn: "N",
      yn_lot: "N",
      yn_sn: "N",
      yn_use: "Y",
      yn_iqc: "N",
      yn_eqc: "N",
      yn_pqc: "N",
      yn_autorcv: "N",
      yn_autobf_prd: "N",
      yn_inqc: "N",
      yn_longterm: "N",
      fg_item: focusedRowNodeH?.data?.fg_item ?? "",
      fg_acct: focusedRowNodeH?.data?.fg_acct ?? "",
      tp_item: focusedRowNodeH?.data?.tp_item ?? "",
      fg_domestic: "D",
      tp_policy: "L4L",
      tp_po: "MRP",
      dt_start: dateToString(new Date()),
      dt_end: "21001231",
      qt_unit_so: 1,
      qt_unit_po: 1,
      qt_max: 1,
      qt_min: 1,
    };
    gridRefs.current["grid1"].current.addRow(newRow);
  };

  const onBeforeAddRow = (): boolean | Record<string, any> => {
    try {
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        // cd_user_reg: userInfo.cd_user,
        nm_uer_reg: userInfo.nm_user,
        fg_vat: "11",
        yn_bf: "Y",
        yn_phn: "N",
        yn_lot: "N",
        yn_sn: "N",
        yn_use: "Y",
        yn_iqc: "N",
        yn_eqc: "N",
        yn_pqc: "N",
        yn_autorcv: "N",
        yn_autobf_prd: "N",
        yn_inqc: "N",
        yn_longterm: "N",
        fg_item: focusedRowNodeH?.data?.fg_item ?? "",
        fg_acct: focusedRowNodeH?.data?.fg_acct ?? "",
        tp_item: focusedRowNodeH?.data?.tp_item ?? "",
        fg_domestic: "D",
        tp_policy: "L4L",
        tp_po: "MRP",
        dt_start: dateToString(new Date()),
        dt_end: "21001231",
        qt_unit_so: 1,
        qt_unit_po: 1,
        qt_max: 1,
        qt_min: 1,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onDelete = async () => {
    const focusedRowNode = gridRefs.current["grid1"]?.current.focusedRowNode();
    let rowIndex: any = 0;

    if (focusedRowNode) {
      rowIndex = focusedRowNode.rowIndex;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      // gridRefs.current["grid1"].current.resetUpdate();
      // gridRefs.current["grid2"].current.resetUpdate();

      if (gridRefs.current["grid1"].current?.getDisplayedRowCount() > 0) {
        const newRowIndex = rowIndex === 0 ? 0 : rowIndex - 1;
        gridRefs.current["grid1"].current?.selectAndFocusRow(newRowIndex);
      } else {
        setFormDatas({
          freeform1: null,
        });
      }
      return;
    }

    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() > 0) {
      const newRowIndex = rowIndex === 0 ? 0 : rowIndex - 1;
      gridRefs.current["grid1"].current?.selectAndFocusRow(newRowIndex);
    } else {
      setFormDatas({
        freeform1: null,
      });
    }
    await onSave();
  };

  const onSave = async () => {
    let masItemChanges, masItemFileChanges;
    try {
      const requiredValuesCheck =
        gridRefs.current["grid1"].current.requiredValuesCheck();

      if (!requiredValuesCheck) {
        return;
      }

      masItemChanges = gridRefs.current["grid1"].current.getChange();
      masItemFileChanges = gridRefs.current["grid2"].current.getChange();
      // SaveData["mas_item"] = gridRefs.current["grid1"].current.getChange();
      // SaveData["mas_itemfile"] = gridRefs.current["grid2"].current.getChange();
      if (
        masItemChanges.modifiedRowcount === 0 &&
        masItemFileChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "저장할 내용이 없습니다.");
        return;
      }

      const gridData: any[] = []; //second search from the grid
      gridRefs.current["grid1"].current.forEachNode((node: any) => {
        gridData.push(node.data);
      });

      const List = new Set<string>();
      if (gridRefs.current["grid1"].current.getDisplayedRowCount() > 1) {
        for (let i = 0; i < gridData.length; i++) {
          const row = gridData[i];
          const Cd_item = row?.cd_item?.trim();

          if (!Cd_item) continue;

          if (List.has(Cd_item)) {
            await showDialog("error", `‘${Cd_item}’ 중복되었습니다.`);
            return;
          }

          List.add(Cd_item);
        }
      }

      const savedCditem = masItemChanges.insert;
      let cdItemList = "";
      for (let i = 0; i < savedCditem.length; i++) {
        cdItemList += savedCditem[i]?.cd_item + ",";
      }

      if (masItemFileChanges.update?.length) {
        masItemFileChanges.update = masItemFileChanges.update.map(
          (row: any) => ({
            ...row,
            cd_user_amd: userInfo.cd_user,
            nm_uer_reg: userInfo.nm_user,
          })
        );
      }

      const newDate = {
        mas_item: masItemChanges,
        itemfile: masItemFileChanges,
      };
      await fnSave("SN_MAS_BAS_REG_002_SAVE_01", newDate);
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {}
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
      return;
    }

    let focusedRowNode = gridRefs.current["grid2"]?.current?.focusedRowNode();
    if (focusedRowNode?.data) {
      const g2 = gridRefs.current["grid2"].current;
      const byKey = focusedRowNode.data.KeyField
        ? g2.getRowNode(focusedRowNode.data.KeyField)
        : null;
      if (byKey) {
        focusedRowNode = byKey;
      } else {
        let found: any = null;
        g2.forEachFilteredNode((n: any) => {
          if (
            !found &&
            n.data?.cd_item === focusedRowNode.data.cd_item &&
            String(n.data?.no_line) === String(focusedRowNode.data.no_line)
          ) {
            found = n;
          }
        });
        focusedRowNode = found;
      }
    }
    if (!focusedRowNode) {
      await showDialog("alter", "첨부파일 행을 선택하세요.");
      return;
    }
    focusedRowNode.setSelected?.(true, true);

    const customPath = `${Menuprops.cd_module}/ITEM/${focusedRowNode.data.cd_item}/`;

    if (id == "deleteRow") {
      const nmFile = focusedRowNode.data.nm_file;
      onDeleteRow();
      if (nmFile) {
        await fileHelperRef.current?.deleteFile(nmFile, customPath);
      }
      return;
    }

    if (id == "btnDownload") {
      if (!focusedRowNode.data.nm_file) {
        await showDialog("alter", "첨부된 파일이 없습니다.");
        return;
      }
      fileHelperRef.current?.downloadFile(
        focusedRowNode.data.nm_file,
        customPath
      );
      return;
    }

    if (id == "btnDelete") {
      // 행 삭제 → grid2 db 저장 → 성공 시 서버 파일 삭제 순서
      if (!focusedRowNode.data.nm_file) {
        await showDialog("alter", "첨부된 파일이 없습니다.");
        return;
      }
      const nmFile = focusedRowNode.data.nm_file;
      onDeleteRow();
      const ok = await saveGrid2Changes();
      if (!ok) return;
      await fileHelperRef.current?.deleteFile(nmFile, customPath);
      return;
    }

    if (id == "btnAttach") {
      let parentUnsaved = false;
      gridRefs.current["grid1"].current?.forEachFilteredNode((node: any) => {
        if (
          node.data?.cd_item === focusedRowNode.data.cd_item &&
          node.data?.state === "insert"
        ) {
          parentUnsaved = true;
        }
      });
      if (parentUnsaved) {
        await showDialog(
          "alter",
          "저장되지 않은 품목입니다. 품목을 먼저 저장하세요."
        );
        return;
      }

      pendingAttachRef.current = {
        node: focusedRowNode,
        prevFile: focusedRowNode.data.nm_file || "",
        customPath,
      };
      fileHelperRef.current?.openFileDialog(customPath);
      return;
    }
  };

  const handleFileUploadSuccess = async (
    fileName: string,
    isUpload: boolean
  ) => {
    if (!isUpload) return;

    const pending = pendingAttachRef.current;
    pendingAttachRef.current = null;

    const targetNode =
      pending?.node ?? gridRefs.current["grid2"].current?.focusedRowNode();
    if (!targetNode) {
      await showDialog("alter", "첨부파일 행을 선택하세요.");
      return;
    }

    gridRefs.current["grid2"].current?.setValueImmediately(
      targetNode,
      "nm_file",
      fileName
    );

    const changes = gridRefs.current["grid2"].current?.getChange();
    if (!changes || changes.modifiedRowcount == 0) {
      await showDialog(
        "error",
        "첨부 내용을 저장하지 못했습니다. 재조회 후 다시 시도해주세요."
      );
      return;
    }

    const ok = await saveGrid2Changes();
    if (ok && pending?.prevFile && pending.prevFile !== fileName) {
      await fileHelperRef.current?.deleteFile(
        pending.prevFile,
        pending.customPath
      );
    }
  };

  const saveGrid2Changes = async () => {
    const fileChanges = gridRefs.current["grid2"].current?.getChange();
    if (!fileChanges || fileChanges.modifiedRowcount == 0) return true;

    const ok = await fnSave("SN_MAS_BAS_REG_002_SAVE_01", {
      itemfile: fileChanges,
    });
    gridRefs.current["grid2"].current?.resetUpdate(!ok);
    return ok;
  };

  const onAddRow = async () => {
    try {
      if (gridRefs.current["grid1"].current.getDisplayedRowCount() === 0) {
        return;
      }

      gridRefs.current["grid1"].current?.stopEditing();
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH == null) {
        alert("선택된 행이 없습니다.");
        return;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      gridRefs.current["grid2"].current?.applyFilters({
        cd_item: focusedRowNodeH.data?.cd_item,
      });

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_user_reg: userInfo.cd_user,
        nm_uer_reg: userInfo.nm_user,

        tp_file: "010",
        cd_item: focusedRowNodeH.data?.cd_item,
        no_line: maxNoLine + 1,
      };

      gridRefs.current["grid2"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDeleteRow = async () => {
    const gridApil = gridRefs.current["grid2"].current;
    const rowCount = gridApil.getDisplayedRowCount();
    if (rowCount === 0) {
      return;
    }
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };

  const onExcelUpload = async () => {
    try {
      const component = (
        <ExcelUpload
          Menuprops={Menuprops}
          PkFields={["cd_item"]}
          tableName="mas_item"
          controllerName="SN_MAS_BAS_REG_002_SAVE_01"
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result?.excelUpload) {
        await onSearch();
      }

      if (result) {
        console.log("엑셀 업로드 완료:", result);
      } else {
        console.log("엑셀 업로드 취소됨");
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcelDownLoad = async () => {
    try {
      // 1) gridRefs.current["grid1"]에서 (필터/정렬 반영)된 행만 수집
      const rows: any[] = [];
      gridRefs.current["grid1"].current?.forEachFilteredNode((node: any) => {
        if (node?.data) rows.push(node.data);
      });

      // 2) gridRefs.current["sheet1"] 초기화 후 주입 (⚠ SnGrid는 setRowData 없음)
      gridRefs.current["sheet1"].current?.deleteAllRows?.(); // SnGrid가 제공하는 삭제 헬퍼
      gridRefs.current["sheet1"].current?.applyTransaction({
        add: rows.map((r) => ({ ...r })), // 깊은 복사 권장
      });

      // 3) “보이는 컬럼만” export 키 생성 (숨김 제외)
      const displayedCols =
        gridRefs.current["sheet1"].current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());

      // 4) Excel Export: processCellCallback으로 “라벨 우선” 매핑
      gridRefs.current["sheet1"].current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
        // onExcelDownLoad 내부 exportDataAsExcel({...})의 processCellCallback 만 교체
      });
    } catch (e: any) {
      showDialog("error", e?.message || "Excel 다운로드 오류");
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    setFormDatas({
      freeform1: selectedRow,
    });
  };

  const onValueChanged = async (event: ValueChangedEvent) => {
    const { colId, newValue } = event;
    switch (colId) {
      case "fg_acct":
        return { fg_expense: "" };

      case "dc_item_unit":
        return { dc_unit_so: newValue, dc_unit_po: newValue };
      default:
        return {};
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onBeforeAddRow,
        },
        filter: { refs: ["grid2"], columns: ["cd_item"] },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        gridref: "grid1",
        etcProps: {},
        events: {
          onValueChanged: onValueChanged,
        },
      },
    }),
    [formControls, formDatas]
  );

  const headers = useMemo(
    () => ({
      grid2: {
        headerInfo: headerInfo.grid2,
        onBtnEtcClick: onGrid2BtnsClick,
        onBtnAddClick: onGrid2BtnsClick,
        onBtnDeleteClick: onGrid2BtnsClick,
      },
    }),
    [headerInfo]
  );

  const search = useMemo(
    () => ({
      data: formData,
      controls: formControlData,
    }),
    [formData, formControlData]
  );

  const dynamicProps = React.useMemo(
    () => ({
      // 1. Ref (JSON의 "ref"와 매칭)
      searchRef,
      // 2. State & Data (JSON의 "_key"와 매칭)
      search,
      grids,
      forms,
      gridRefs,
      formRefs,
      headers,
    }),
    [
      // Data State가 변경될 때만 dynamicProps가 새로 생성되도록 의존성 지정
      formData,
      formControlData,
      grids,
      forms,
      gridRefs,
      formRefs,
      headers,
    ]
  );
  return (
    <>
      <ComCreateUi dynamicProps={dynamicProps} layoutConfig={layout} />
      <SnFileHelper
        ref={fileHelperRef}
        onFileUploadSuccess={handleFileUploadSuccess}
      />
    </>
  );
});

export default SN_MAS_BAS_REG_002;
